import CircularProgress from '@mui/material/CircularProgress'
import { PlayIcon } from 'components/Icons'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import { useMemo } from 'react'
import { BulkContentPost } from 'services/compose/state'
import { randomId } from 'services/compose/utils'
import { BRAND_INSTAGRAM, POST_TYPE_STORY } from 'shared/constants'
import { ComposerInstagramPost } from 'shared/types'
import styles from './BulkPost.pcss'
import { isVideoPost } from './postMedia'

interface BulkPostMediaProps {
  post: BulkContentPost
  onMediaClick: (params: { url: string, type: 'image' | 'video', selector?: string }) => void
}

export function BulkPostMedia(props: BulkPostMediaProps) {
  const { post } = props
  const { article, imageUrl, content } = post
  const isArticle = Boolean(article)
  const isVideo = isVideoPost(post)

  const upload = post.imageUpload
  const isUploading = upload && !upload.error && !upload.url
  const imageSrc = imageUrl || upload?.src || content?.imageUrl

  const igData = post.network === BRAND_INSTAGRAM ? post.data as ComposerInstagramPost : undefined
  // EXPL: Stories are published without a cover image, so a cover set before switching to Story is ignored
  const igIsStory = igData?.postType === POST_TYPE_STORY
  // The row's poster is a background-image div (the raw <video> collapses to 0x0 in this
  // fixed-width cell without a poster). Prefer the uploaded IG cover, else the video thumbnail.
  const posterUrl = (igIsStory ? undefined : igData?.coverUrl) || post.videoThumbnailUrl

  // Cache-bust once per video url — regenerating per render remounts the player and refetches the video.
  const videoSrc = useMemo(() => (post.videoUrl ? `${post.videoUrl}?v=${randomId()}` : undefined), [post.videoUrl])

  const articlePreviewStyles = {
    backgroundImage: `url(${article?.imageUrl})`
  }

  const handleMediaClick = () => {
    const selector = post.videoUrl ? undefined : `[data-testid="post-img-${post.id}"]`
    const url = post.videoUrl || imageUrl
    if (url) {
      props.onMediaClick({ url, type: post.videoUrl ? 'video' : 'image', selector })
    }
  }

  return (
    <div className={styles['media-box']}>
      <div className={styles['media-box-inner']}>
        {isArticle && (
          <div className={styles['article-preview']} style={articlePreviewStyles}>
            <div className={styles.heading}>
              <h4>
                <a href={article?.url} target="_blank" rel="noopener">
                  {article?.title}
                </a>
              </h4>
            </div>
          </div>
        )}
        {imageSrc && !posterUrl && (
          <img src={imageSrc} className={styles.img} data-testid={`post-img-${post.id}`} onClick={handleMediaClick} />
        )}
        {isUploading && (
          <div className={styles['loading-overlay']}>
            <CircularProgress size={20} className={styles.loader} />
          </div>
        )}
        {isVideo && (
          <PlayIcon className={styles['icon-play']} />
        )}
        {posterUrl && (
          <div
            className={styles['video-thumbnail']}
            data-testid={`post-poster-${post.id}`}
            onClick={handleMediaClick}
            style={{ backgroundImage: `url(${posterUrl})` }}
          />
        )}
        {videoSrc && !posterUrl && !imageSrc && (
          <NativeVideoPlayer
            mediaElementId={`post-video-${post.id}`}
            videoUrl={videoSrc}
            playBtnClassName={styles['btn-play']}
            onClick={handleMediaClick}
          />
        )}
      </div>
      {post.content?.socialLink && (
        <a className={styles.olink} href={post.content.socialLink} target="_blank" rel="noopener">See original</a>
      )}
    </div>
  )
}
